import { isArray, isDefined, isNil, isString, isSymbol } from './guards';

/**
 * Truncates long string to the specified length
 * @param {string} text
 * @param {number | undefined} length
 * @param {string | undefined} suffix
 * @return {string}
 */
export const truncate = (text: string, length?: number, suffix: string = '…'): string =>
	isDefined(length) && text.length > length ? `${text.substring(0, length - suffix.length)}${suffix}` : text;

/**
 * Encodes string to base64
 * @param {string} data
 * @returns {string}
 */
export const encodeBase64 = (data: string) => Buffer.from(data).toString('base64');

/**
 * Decodes base64 string
 * @param {string} data
 * @returns {string}
 */
export const decodeBase64 = (data: string) => Buffer.from(data, 'base64').toString('ascii');

/**
 * Pad string to fixed length
 * @param input Input string or number
 * @param size Size to pad to
 * @param filler Filler character, default is '0'
 * @returns Padded string to fixed length
 */
export const padStringToFixed = (input: string | number, size: number, filler: string = '0') => {
	if (size === 0) return '';
	const filled = filler.repeat(size);
	return `${filled}${input}`.slice(-size);
};

/**
 * Transforms input string to camelCase
 * @param str An input string
 * @returns CamelCased string
 */
export const camelCase = (str: string) => {
	/* handle empty string case */
	if (str.length === 0) return '';
	/* split the string into words */
	let words = str.split(/[_\-.\s]+/).filter((word) => word.length > 0);
	/* if we only have one word, split it by capital letters */
	if (words.length === 1) {
		words = words[0]!.split(/(?=[A-Z])/).map((word) => word.toLowerCase());
	}
	/* transform words */
	return words
		.map((word, index) => {
			/** convert to lower case */
			word = word.toLowerCase();
			/* capitalize first letter for all but the first word */
			if (index !== 0) {
				word = word.charAt(0).toUpperCase() + word.slice(1);
			}

			return word;
		})
		.join('');
};

/** Regular expression to match words */
const wordPattern = new RegExp(['[A-Z][a-z]+', '[A-Z]+(?=[A-Z][a-z])', '[A-Z]+', '[a-z]+', '[0-9]+'].join('|'), 'g');

/**
 * Separates string into words
 * @param str Input string
 * @param pattern Optional pattern to match
 * @returns Array of words
 */
export const words = (str = '', pattern?: RegExp | string): string[] => {
	if (pattern === undefined) return str.match(wordPattern) || [];
	return str.match(pattern) || [];
};

/**
 * Transforms input string to kebab-case
 * @param str An input string
 * @returns Kebab-cased string
 */
export const snakeCase = (str = ''): string =>
	words(str)
		.map((word) => word.toLowerCase())
		.join('_');

/**
 * Capitalizes the first letter of a string
 * @param str String to capitalize
 * @returns Capitalized string
 */
export const upperFirst = (str: string): string => str.slice(0, 1).toUpperCase() + str.slice(1);

const symbolProto = Symbol.prototype;

/**
 * Converts the given value to a string
 * @param value Any value to convert to a string
 * @returns String representation of the given value
 */
export const toString = (value: any): string => {
	if (isNil(value)) {
		return '';
	}
	if (isString(value)) {
		return value;
	}
	if (isArray(value)) {
		return value.map((arrVal) => toString(arrVal)) + '';
	}
	if (isSymbol(value)) {
		return symbolProto.toString.call(value);
	}
	const result = value + '';
	return result === '0' && 1 / value == -Infinity ? '-0' : result;
};

/**
 * Simple hashing function for strings to number.
 * @param str String to hash
 * @returns Hashed number
 */
export const hashString = (str: string): number => {
	let hash = 0;
	if (str.length === 0) return hash;
	for (let i = 0; i < str.length; i++) {
		const char = str.charCodeAt(i);
		hash = (hash << 5) - hash + char;
		hash = hash & hash;
	}
	return hash;
};
